'use client'

import {ReactNode, useRef, useEffect, useState, FC} from 'react'
import s from './index.module.css'
import throttle from 'lodash.throttle'
import ArrowButton from './slider-arrow-button'
import {useSliderAnalytics} from './analytics-context'

type Props = {
  children: ReactNode
  arrowsVisible: boolean
}

const SliderClient: FC<Props> = ({children, arrowsVisible}) => {
  const slideTrack = useRef<HTMLDivElement>(null)
  const [isLeftArrowVisible, setLeftArrowVisible] = useState(false)
  const [isRightArrowVisible, setRightArrowVisible] = useState(true)

  // analytics callback (will be a no-op if not inside AnalyticsProvider)
  const {onSlideLeft} = useSliderAnalytics()

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (slideTrack.current) {
        const {scrollLeft, scrollWidth, clientWidth} = slideTrack.current
        setLeftArrowVisible(scrollLeft > 0)
        setRightArrowVisible(scrollLeft < scrollWidth - clientWidth)
        if (scrollLeft > 0) {
          onSlideLeft()
        }
      }
    }, 100)

    const currentSlideTrack = slideTrack.current
    currentSlideTrack?.addEventListener('scroll', handleScroll, {passive: true})
    handleScroll() // Call handleScroll initially to set the initial state of the arrows

    return () => currentSlideTrack?.removeEventListener('scroll', handleScroll)
  }, [])

  const slide = (direction: 'left' | 'right') => {
    if (slideTrack.current) {
      const scrollAmount = direction === 'left' ? -400 : 400
      slideTrack.current.scrollBy({left: scrollAmount, behavior: 'smooth'})
    }
  }

  return (
    <div className={s.sliderContainer}>
      {isLeftArrowVisible && arrowsVisible && <ArrowButton direction='left' onClick={() => slide('left')} />}
      <div ref={slideTrack} className={s.sliderTrack}>
        {children}
      </div>
      {isRightArrowVisible && arrowsVisible && <ArrowButton direction='right' onClick={() => slide('right')} />}
    </div>
  )
}

export default SliderClient
